import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { TrustedAdvisor, UpdateTrustedAdvisorRequest } from '../../types/trustedAdvisor';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { Users, Edit } from 'lucide-react';

interface TrustedAdvisorEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  advisor: TrustedAdvisor;
  onSave: () => void;
}

const TrustedAdvisorEditModal: React.FC<TrustedAdvisorEditModalProps> = ({
  isOpen,
  onClose,
  advisor,
  onSave
}) => {
  const [formData, setFormData] = useState({
    first_name: advisor.first_name,
    last_name: advisor.last_name,
    relationship: advisor.relationship
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.relationship.trim()) {
      setError('All fields are required');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const req: UpdateTrustedAdvisorRequest = {
        advisor_id: advisor.id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        relationship: formData.relationship
      };
      await TrustedAdvisorService.updateTrustedAdvisor(req);
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update advisor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <div className="flex items-center mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <Edit className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Edit Trusted Advisor
            </h2>
            <p className="text-sm text-gray-600">
              Update advisor information and relationship
            </p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                placeholder="Enter first name"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Enter last name"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relationship *
            </label>
            <Input
              type="text"
              value={formData.relationship}
              onChange={(e) => handleInputChange('relationship', e.target.value)}
              placeholder="e.g., Spouse, Attorney, Financial Advisor, Friend, etc."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Describe your relationship with this person (e.g., "My attorney", "Best friend", "Financial advisor")
            </p>
          </div>
          
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default TrustedAdvisorEditModal; 